import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { FaEye } from 'react-icons/fa';
import RecallCheck from '../RecallCheck';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import { usePremium } from '../Subscription/PremiumProvider';
import { isLocked } from '../../lib/premiumLock';
import {
  computeDueRoundSummary,
  formatItemCount,
} from './dueRoundSummary';
import BasketNameChip from './BasketNameChip';

/** Dedupes StrictMode double-mount for due_tab opens (same workspace, <500ms). */
const recentDueTabOpens = new Map();

const getHardDetail = (topic) =>
  topic?.concepts?.find((c) => c.is_highlighted)?.text || '';

/**
 * Next index after `fromIndex` (wrapping) whose topic isn't done yet.
 * Returns -1 when everything is done.
 */
const findNextIndex = (queueIds, doneIds, fromIndex) => {
  const total = queueIds.length;
  for (let step = 1; step <= total; step += 1) {
    const idx = (fromIndex + step) % total;
    if (!doneIds.has(queueIds[idx])) return idx;
  }
  return -1;
};

const sectionLabelClass =
  'text-[11px] font-semibold uppercase tracking-wider text-neutral-400';

/**
 * One due card. Keyed by topic id in the parent, so reveal state resets
 * automatically when the session moves to the next topic.
 */
const SiblingPreviewModal = ({ topic, onClose }) => {
  const { isPremium, isLoading } = usePremium();
  const closeButtonRef = useRef(null);
  const hardDetail = getHardDetail(topic);
  const topicLocked = isLocked(topic, isPremium, isLoading);

  useEffect(() => {
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-neutral-900/40"
        aria-label="Close topic preview"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sibling-preview-title"
        className="relative z-10 w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-6 shadow-lg"
      >
        <div className="flex items-start justify-between gap-3">
          <h3
            id="sibling-preview-title"
            className="text-2xl font-semibold leading-tight text-neutral-900"
          >
            {topic.name}
          </h3>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg px-2 py-1 text-sm text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-cranberry-700"
          >
            Close
          </button>
        </div>

        {topic.is_ripe && (
          <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#C9982E] px-2 py-0.5 text-[11px] font-medium text-neutral-900">
            Ripe
          </span>
        )}

        {topicLocked ? (
          <section className="mt-6 flex items-start gap-2 text-sm text-neutral-500">
            <Lock size={14} className="shrink-0 mt-0.5" aria-hidden="true" />
            <span>Connection details are Premium</span>
          </section>
        ) : topic.relation_description ? (
          <section className="mt-6">
            <p className={sectionLabelClass}>Why it&apos;s here</p>
            <p className="mt-2 text-base italic leading-relaxed text-neutral-900">
              {topic.relation_description}
            </p>
          </section>
        ) : (
          <p className="mt-6 text-sm italic text-neutral-400">
            No relationship description yet.
          </p>
        )}

        {!topicLocked && hardDetail ? (
          <section className="mt-6">
            <p className={sectionLabelClass}>Difficult detail</p>
            <div className="mt-2 rounded-r-lg border-l-4 border-blue-600 bg-blue-50/70 py-3 pl-4 pr-4">
              <p className="text-base leading-relaxed text-blue-900">{hardDetail}</p>
            </div>
          </section>
        ) : !topicLocked ? (
          <p className="mt-6 text-sm text-neutral-400">
            No highlighted detail on this topic.
          </p>
        ) : null}
      </div>
    </div>
  );
};

const DueCard = ({
  topic,
  basket,
  workspaceId,
  doneCount,
  total,
  autoFocus = false,
  onRecallComplete,
}) => {
  const { isPremium, isLoading } = usePremium();
  const [isRevealed, setIsRevealed] = useState(false);
  const [previewSibling, setPreviewSibling] = useState(null);
  const revealButtonRef = useRef(null);
  const revealedRef = useRef(null);
  const openerChipRef = useRef(null);
  const topicLocked = isLocked(topic, isPremium, isLoading);

  // Focus the reveal button only after advance / jump — not on first mount
  // (would steal focus from the search box).
  useEffect(() => {
    if (autoFocus) revealButtonRef.current?.focus();
  }, [autoFocus]);

  // The reveal button unmounts on tap; move focus into the revealed content so
  // keyboard users don't get dropped back to the top of the page.
  useEffect(() => {
    if (isRevealed) revealedRef.current?.focus({ preventScroll: true });
  }, [isRevealed]);

  const hardDetail = getHardDetail(topic);
  const siblings = (basket?.topics || []).filter((t) => t.id !== topic.id);

  const handleReveal = () => {
    setIsRevealed(true);
    captureConnectionsEvent('due_for_review_revealed', {
      workspace_id: workspaceId,
      topic_id: topic.id,
      basket_id: topic.basket_id ?? undefined,
    });
  };

  const handleOpenPreview = (sibling, event) => {
    openerChipRef.current = event.currentTarget;
    setPreviewSibling(sibling);
  };

  const handleClosePreview = useCallback(() => {
    const chip = openerChipRef.current;
    setPreviewSibling(null);
    requestAnimationFrame(() => {
      chip?.focus({ preventScroll: true });
    });
  }, []);

  const eyebrow =
    total > 1 ? `Question ${doneCount + 1} of ${total}` : 'Due today';

  return (
    <article className="min-w-0 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5">
      <p className={`${sectionLabelClass} mb-2.5`}>{eyebrow}</p>

      <h2 className="text-xl sm:text-2xl font-semibold leading-normal text-neutral-900">
        {basket?.name ? (
          <>
            Why is <span className="font-bold">{topic.name}</span> in the{' '}
            <BasketNameChip name={basket.name} />{' '}
            basket?
          </>
        ) : (
          <>
            What do you remember about <span className="font-bold">{topic.name}</span>?
          </>
        )}
      </h2>

      {!isRevealed ? (
        <>
          <p className="mt-4 mb-2 text-[15px] text-neutral-400">
            {basket?.name ? (
              <>
                Start from what you know about{' '}
                <span className="font-medium text-neutral-500">{basket.name}</span>.
                What do they share?
              </>
            ) : (
              'Try to recall first.'
            )}
          </p>
          <button
            ref={revealButtonRef}
            type="button"
            onClick={handleReveal}
            aria-label={`Reveal answer for ${topic.name}`}
            className="mt-4 flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-neutral-400 bg-neutral-100 px-4 py-6 text-neutral-900 transition-colors duration-200 hover:border-neutral-500 hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cranberry-700 focus-visible:ring-offset-2"
          >
            <span className="inline-flex items-center gap-2 text-base font-medium">
              <FaEye size={15} aria-hidden="true" />
              Reveal answer
            </span>
          </button>
        </>
      ) : (
        <div ref={revealedRef} tabIndex={-1} className="mt-3 outline-none">
          {topicLocked ? (
            <section className="mt-5 flex items-start gap-2 text-sm text-neutral-500">
              <Lock size={14} className="shrink-0 mt-0.5" aria-hidden="true" />
              <span>Connection details are Premium</span>
            </section>
          ) : (
            <>
              {topic.relation_description && (
                <section className="mt-5">
                  <p className={sectionLabelClass}>Why it&apos;s here</p>
                  <p className="mt-1 text-base italic leading-snug text-neutral-900 sm:text-lg">
                    {topic.relation_description}
                  </p>
                </section>
              )}

              {hardDetail && (
                <section className="mt-5">
                  <p className={sectionLabelClass}>Difficult detail</p>
                  <div className="mt-1 rounded-r-md border-l-4 border-blue-600 bg-blue-50/70 py-2 pl-3 pr-3">
                    <p className="text-base leading-snug text-blue-900">
                      {hardDetail}
                    </p>
                  </div>
                </section>
              )}
            </>
          )}

          {siblings.length > 0 && (
            <section className="mt-5 min-w-0">
              <p className={sectionLabelClass}>
                Connected topics · {siblings.length}
              </p>
              <div className="relative mt-2 min-w-0 max-w-full">
                <ul className="flex items-center gap-1.5 overflow-x-auto snap-x snap-mandatory pb-1.5 pr-8 [scrollbar-width:thin]">
                  {siblings.map((sibling) => (
                    <li key={sibling.id} className="shrink-0 snap-start">
                      <button
                        type="button"
                        onClick={(event) => handleOpenPreview(sibling, event)}
                        title={sibling.name}
                        className="inline-flex max-w-[10rem] items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-left transition-colors hover:border-neutral-400 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cranberry-700"
                        aria-label={`${sibling.name}${sibling.is_ripe ? ', ripe' : ''}. View connections`}
                      >
                        <span className="truncate text-xs font-medium text-neutral-800">
                          {sibling.name}
                        </span>
                        {sibling.is_ripe && (
                          <span
                            className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#C9982E]"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
                <div
                  className="pointer-events-none absolute right-0 top-0 bottom-1.5 w-8 bg-gradient-to-l from-white to-transparent"
                  aria-hidden="true"
                />
              </div>
            </section>
          )}

          {!topicLocked && (
            <div className="mt-5">
              <RecallCheck
                topicId={topic.id}
                topicName={topic.name}
                onRecallComplete={onRecallComplete}
              />
            </div>
          )}
        </div>
      )}

      {previewSibling && (
        <SiblingPreviewModal topic={previewSibling} onClose={handleClosePreview} />
      )}
    </article>
  );
};

/**
 * Focused Due-for-review session: one card at a time, auto-advancing.
 * doneIds is lifted to the parent so tab switches resume mid-session.
 *
 * @param {{
 *   queueIds: string[],
 *   startIndex?: number,
 *   baskets: Array,
 *   topics: Array,
 *   workspaceId: string,
 *   doneIds: Set<string>,
 *   remainingDueCount?: number,
 *   onTopicDone: (id: string) => void,
 *   onStartNextRound?: (kind: 'retry' | 'keep_going') => void,
 *   onExit: () => void,
 * }} props
 */
const EMPTY_DONE_IDS = new Set();

const DueSession = ({
  queueIds,
  startIndex = 0,
  baskets,
  topics,
  workspaceId,
  doneIds = EMPTY_DONE_IDS,
  remainingDueCount = 0,
  onTopicDone,
  onStartNextRound,
  onExit,
}) => {
  // Freeze the queue for the life of the session.
  const [queue] = useState(queueIds);
  const [currentIndex, setCurrentIndex] = useState(() => {
    const firstUndone = findNextIndex(queueIds, doneIds, -1);
    if (firstUndone !== -1) return firstUndone;
    if (typeof startIndex === 'number' && startIndex >= 0) return startIndex;
    return 0;
  });
  const [isComplete, setIsComplete] = useState(() =>
    queueIds.length > 0 && findNextIndex(queueIds, doneIds, -1) === -1
  );
  const [autoFocusReveal, setAutoFocusReveal] = useState(false);
  const dueTabOpenedRef = useRef(false);
  const endHeadingRef = useRef(null);
  const topicsRef = useRef(topics);
  const remainingDueCountRef = useRef(remainingDueCount);
  topicsRef.current = topics;
  remainingDueCountRef.current = remainingDueCount;

  const topicsById = useMemo(() => {
    const map = new Map();
    (topics || []).forEach((t) => map.set(t.id, t));
    return map;
  }, [topics]);

  const currentId = queue[currentIndex];
  const currentTopic = topicsById.get(currentId);
  const currentBasket = currentTopic
    ? (baskets || []).find((b) => b.id === currentTopic.basket_id) || null
    : null;

  const total = queue.length;
  const doneCount = queue.filter((id) => doneIds.has(id)).length;

  const summary = useMemo(
    () =>
      computeDueRoundSummary({
        doneIds,
        topics,
        remainingDueCount,
      }),
    [doneIds, topics, remainingDueCount]
  );

  // First card of this mount: fire due_tab once (StrictMode-safe).
  useEffect(() => {
    if (dueTabOpenedRef.current) return;
    if (isComplete || !currentTopic) return;

    const now = Date.now();
    const previous = recentDueTabOpens.get(workspaceId);
    if (previous && now - previous < 500) {
      dueTabOpenedRef.current = true;
      return;
    }
    recentDueTabOpens.set(workspaceId, now);
    dueTabOpenedRef.current = true;

    captureConnectionsEvent('due_for_review_item_opened', {
      workspace_id: workspaceId,
      topic_id: currentTopic.id,
      basket_id: currentTopic.basket_id ?? undefined,
      source: 'due_tab',
    });
  }, [isComplete, currentTopic, workspaceId]);

  useEffect(() => {
    if (!isComplete) return;
    endHeadingRef.current?.focus();
  }, [isComplete]);

  const handleAdvance = () => {
    onTopicDone?.(currentId);

    const nextDone = new Set(doneIds);
    nextDone.add(currentId);

    const nextIndex = findNextIndex(queue, nextDone, currentIndex);
    if (nextIndex === -1) {
      setIsComplete(true);
      const completedSummary = computeDueRoundSummary({
        doneIds: nextDone,
        topics: topicsRef.current,
        remainingDueCount: remainingDueCountRef.current,
      });
      captureConnectionsEvent('due_for_review_session_completed', {
        workspace_id: workspaceId,
        total: completedSummary.total,
        recalled_count: completedSummary.recalledCount,
        not_recalled_count: completedSummary.notRecalled,
        remaining: completedSummary.remaining,
      });
      return;
    }

    setAutoFocusReveal(true);
    setCurrentIndex(nextIndex);
    const nextTopic = topicsById.get(queue[nextIndex]);
    captureConnectionsEvent('due_for_review_item_opened', {
      workspace_id: workspaceId,
      topic_id: queue[nextIndex],
      basket_id: nextTopic?.basket_id ?? undefined,
      source: 'auto_advance',
    });
  };

  const handlePrimaryNext = () => {
    if (!onStartNextRound) return;
    const kind = summary.untested > 0 ? 'keep_going' : 'retry';
    onStartNextRound(kind);
  };

  if (isComplete) {
    const {
      total: roundTotal,
      recalledCount,
      notRecalled,
      remaining,
      untested,
    } = summary;
    const isAllCaughtUp = remaining === 0;

    const line1 = isAllCaughtUp
      ? formatItemCount(roundTotal)
      : untested === 0
        ? formatItemCount(roundTotal)
        : `${formatItemCount(roundTotal)} this round`;

    const line2 = isAllCaughtUp
      ? `${recalledCount} recalled. Nice work.`
      : [
          recalledCount > 0 ? `${recalledCount} recalled` : null,
          notRecalled > 0 ? `${notRecalled} not recalled` : null,
          untested > 0 ? `${untested} more due today` : null,
        ]
          .filter(Boolean)
          .join(' · ');

    const primaryLabel =
      untested === 0
        ? notRecalled === 1
          ? 'Try that one again'
          : `Try those ${notRecalled} again`
        : 'Keep going';

    return (
      <div
        className="rounded-2xl border border-neutral-200 bg-white p-10 text-center shadow-sm"
        role="status"
      >
        <h2
          ref={endHeadingRef}
          tabIndex={-1}
          className="text-3xl font-semibold text-neutral-900 outline-none"
        >
          {isAllCaughtUp ? 'All caught up' : 'Round done'}
        </h2>
        <p className="mt-3 text-sm font-semibold text-neutral-800">{line1}</p>
        <p className="mt-1 text-sm text-neutral-600">{line2}</p>
        <div className="mt-6 flex flex-col items-center gap-3">
          {isAllCaughtUp ? (
            <button
              type="button"
              onClick={onExit}
              className="min-h-[44px] rounded-full bg-cranberry-700 px-6 py-2 text-sm font-medium text-white hover:bg-cranberry-800 focus:outline-none focus:ring-2 focus:ring-cranberry-700 focus:ring-offset-2"
            >
              Browse baskets
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handlePrimaryNext}
                className="min-h-[44px] rounded-full bg-cranberry-700 px-6 py-2 text-sm font-medium text-white hover:bg-cranberry-800 focus:outline-none focus:ring-2 focus:ring-cranberry-700 focus:ring-offset-2"
              >
                {primaryLabel}
              </button>
              <button
                type="button"
                onClick={onExit}
                className="min-h-[44px] px-3 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-cranberry-700"
              >
                Done for now
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!currentTopic) {
    return (
      <div
        className="rounded-2xl border border-neutral-200 bg-white p-10 text-center shadow-sm"
        role="status"
      >
        <p className="text-sm text-neutral-600">Nothing left in this round.</p>
        <button
          type="button"
          onClick={onExit}
          className="mt-6 min-h-[44px] text-sm font-medium text-cranberry-700 hover:text-cranberry-800"
        >
          Browse baskets
        </button>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <DueCard
        key={currentTopic.id}
        topic={currentTopic}
        basket={currentBasket}
        workspaceId={workspaceId}
        doneCount={doneCount}
        total={total}
        autoFocus={autoFocusReveal}
        onRecallComplete={handleAdvance}
      />
    </div>
  );
};

export default DueSession;
